// core.js
(function (global) {
  'use strict';

  // Основной объект WebAim
  const WA = {
    utils: {},

    // Модуль управления ресурсами
    resources: {
      _loaded: new Map(), // Нормализованные URL загруженных ресурсов
      _loading: new Map(), // Ресурсы в процессе загрузки

      /**
       * Загрузка ресурсов с защитой от дублирования
       * @param {Object} options - { scripts: [{src, attrs}], styles: [{href, attrs}] }
       * @returns {Promise<{loaded: string[], failed: string[]}>}
       */
      load: function (options) {
        const normalizeUrl = (url) =>
          url
            .replace(/^https?:\/\//, '')
            .split('?')[0]
            .split('#')[0]
            .toLowerCase();

        const filterResources = (items) =>
          (items || []).filter((item) => {
            const url = normalizeUrl(item.src || item.href);
            return !this._loaded.has(url) && !this._loading.has(url);
          });

        const scriptsToLoad = filterResources(options.scripts);
        const stylesToLoad = filterResources(options.styles);

        if (scriptsToLoad.length === 0 && stylesToLoad.length === 0) {
          return Promise.resolve({ loaded: [], failed: [] });
        }

        [...scriptsToLoad, ...stylesToLoad].forEach((item) => {
          this._loading.set(normalizeUrl(item.src || item.href), true);
        });

        const scriptPromises = scriptsToLoad.map(
          (item) =>
            new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = item.src;
              Object.entries(item.attrs || {}).forEach(([k, v]) => script.setAttribute(k, v));

              script.onload = () => {
                this._loaded.set(normalizeUrl(item.src), true);
                this._loading.delete(normalizeUrl(item.src));
                resolve(item.src);
              };
              script.onerror = () => {
                this._loading.delete(normalizeUrl(item.src));
                reject(item.src);
              };
              document.head.appendChild(script);
            })
        );

        const stylePromises = stylesToLoad.map(
          (item) =>
            new Promise((resolve, reject) => {
              const link = document.createElement('link');
              link.rel = 'stylesheet';
              link.href = item.href;
              Object.entries(item.attrs || {}).forEach(([k, v]) => link.setAttribute(k, v));

              link.onload = () => {
                this._loaded.set(normalizeUrl(item.href), true);
                this._loading.delete(normalizeUrl(item.href));
                resolve(item.href);
              };
              link.onerror = () => {
                this._loading.delete(normalizeUrl(item.href));
                reject(item.href);
              };
              document.head.appendChild(link);
            })
        );

        return Promise.allSettled([...scriptPromises, ...stylePromises]).then((results) => {
          const loaded = [];
          const failed = [];
          const items = [...scriptsToLoad, ...stylesToLoad];

          results.forEach((result, i) => {
            const url = items[i].src || items[i].href;
            result.status === 'fulfilled' ? loaded.push(url) : failed.push(url);
          });

          if (global.WA && global.WA.events) {
            global.WA.events.emit('resources:loaded', { loaded, failed });
          }

          return { loaded, failed };
        });
      },

      /**
       * Проверка загрузки ресурса
       * @param {string} url - URL ресурса
       * @returns {boolean}
       */
      isLoaded: function (url) {
        const normalized = url
          .replace(/^https?:\/\//, '')
          .split('?')[0]
          .split('#')[0]
          .toLowerCase();
        return this._loaded.has(normalized);
      },

      /**
       * Загружает модуль утилит и добавляет его в WA.utils
       * @param {string} moduleName - Имя модуля (будет доступно как WA.utils[moduleName])
       * @param {string} scriptUrl - URL скрипта с реализацией утилит
       * @returns {Promise<void>}
       */
      loadUtility: function (moduleName, scriptUrl) {
        return new Promise((resolve, reject) => {
          if (WA.utils[moduleName]) {
            console.log(`Utility module "${moduleName}" already loaded`);
            return resolve();
          }

          WA.resources
            .load({
              scripts: [{ src: scriptUrl }],
            })
            .then(({ loaded, failed }) => {
              if (failed.length > 0) {
                console.error(`Failed to load utility "${moduleName}"`);
                return reject(new Error(`Utility load failed: ${failed[0]}`));
              }

              if (!WA.utils[moduleName]) {
                console.error(`Utility "${moduleName}" not attached after load`);
                return reject(new Error('Utility not properly initialized'));
              }

              console.log(`Utility "${moduleName}" loaded successfully`);
              resolve();
            });
        });
      },
    },

    // Система событий
    events: {
      _listeners: {},

      /**
       * Подписка на событие
       * @param {string} event - Имя события
       * @param {Function} callback - Обработчик
       */
      on: function (event, callback) {
        if (!this._listeners[event]) {
          this._listeners[event] = [];
        }
        this._listeners[event].push(callback);
      },

      /**
       * Отмена подписки
       * @param {string} event - Имя события
       * @param {Function} callback - Обработчик
       */
      off: function (event, callback) {
        if (!this._listeners[event]) return;
        this._listeners[event] = this._listeners[event].filter((cb) => cb !== callback);
      },

      /**
       * Вызов события
       * @param {string} event - Имя события
       * @param {any} data - Данные события
       */
      emit: function (event, data) {
        if (!this._listeners[event]) return;
        this._listeners[event].forEach((callback) => {
          try {
            callback(data);
          } catch (e) {
            console.error(`Event handler error for ${event}:`, e);
          }
        });
      },
    },

    // Управление зависимостями
    dependencies: {
      _requests: new Map(), // Хранит {resourceKey: [callbacks]}

      /**
       * Загрузка зависимостей с последующим вызовом callback
       * @param {Object} resources - {scripts: [], styles: []}
       * @param {Function} callback - Функция после загрузки
       */
      require: function (resources, callback) {
        const resourceKey = this._getResourceKey(resources);

        if (this._checkLoaded(resources)) {
          callback();
          return;
        }

        if (this._requests.has(resourceKey)) {
          this._requests.get(resourceKey).push(callback);
          return;
        }

        this._requests.set(resourceKey, [callback]);

        WA.resources.load(resources).then(({ loaded, failed }) => {
          if (failed.length === 0) {
            this._requests.get(resourceKey).forEach((cb) => cb());
          }
          this._requests.delete(resourceKey);
        });
      },

      /**
       * Проверка загрузки всех ресурсов
       * @private
       */
      _checkLoaded: function (resources) {
        const scriptsLoaded = !resources.scripts || resources.scripts.every((s) => WA.resources.isLoaded(s.src));
        const stylesLoaded = !resources.styles || resources.styles.every((s) => WA.resources.isLoaded(s.href));
        return scriptsLoaded && stylesLoaded;
      },

      /**
       * Генерация ключа для ресурсов
       * @private
       */
      _getResourceKey: function (resources) {
        return JSON.stringify({
          scripts: resources.scripts?.map((s) => s.src),
          styles: resources.styles?.map((s) => s.href),
        });
      },
    },
  };

  // Экспорт в глобальную область
  global.WA = WA;
  Object.defineProperty(global, 'WA', {
    value: WA,
    writable: false,
    configurable: false,
  });

  // Initial core loading
  WA.resources
    .load({
      scripts: [
        { src: '/assets/scripts/common.js' },
        { src: 'https://vjs.zencdn.net/8.23.4/video.min.js' },
        // Можно добавить базовые утилиты здесь
        // { src: '/assets/scripts/extensions/forms/validator.js' },
      ],
      styles: [{ href: 'https://vjs.zencdn.net/8.23.4/video-js.css' }],
    })
    .then(() => {
      return WA.resources.load({
        scripts: [{ src: 'https://cdn.jsdelivr.net/npm/videojs-contrib-quality-menu/dist/videojs-contrib-quality-menu.min.js' }],
      });
    })
    .then(({ loaded, failed }) => {
      if (loaded.length > 0) {
        WA.events.emit('WA:ready');
      }
      if (failed.length > 0) {
        console.error('Initial scripts load failed:', failed);
        WA.events.emit('WA:error', { failed });
      }
    });
})(window);
